import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCheck, MessageCircleWarning, Bot, TrendingUp, ArrowRight } from 'lucide-react';
import Reveal from './Reveal';
import ParallaxBlob from './ParallaxBlob';
import introBg from '../../assets/iphone-wallpaper.png';
import introBgVideo from '../../assets/iphone-wallpaper-animado-2.mp4';

// accent por paso — mismo criterio que el resto de la página (why-cards de
// AboutUs.tsx): cada tarjeta con su propio color en vez de las 3 en
// monotono, y coherente con lo que representa cada beat (problema = ámbar
// de alerta, solución = teal de marca/IA, resultado = verde de progreso).
const steps = [
  {
    icon: MessageCircleWarning,
    accent: '#fbbf24',
    eyebrow: 'about_page.history_beat_2_eyebrow',
    eyebrowFallback: '01 — Origen',
    titleKey: 'history_problem_title',
    titleFallback: 'El problema',
    descKey: 'about_page.history_beat_2',
    descFallback: 'El mayor reto en la era digital es la velocidad. Un mensaje no contestado en minutos es una venta o un paciente perdido.',
  },
  {
    icon: Bot,
    accent: '#00e5d1',
    eyebrow: 'about_page.history_beat_3_eyebrow',
    eyebrowFallback: '02 — Respuesta',
    titleKey: 'history_solution_title',
    titleFallback: 'La solución',
    descKey: 'about_page.history_beat_3',
    descFallback: 'Construimos agentes de IA que no duermen. Analizan, responden y agendan 24/7 con empatía humana y precisión de máquina.',
  },
  {
    icon: TrendingUp,
    accent: '#34d399',
    eyebrow: 'about_page.history_beat_4_eyebrow',
    eyebrowFallback: '03 — Impacto',
    titleKey: 'history_result_title',
    titleFallback: 'El resultado',
    descKey: 'about_page.history_beat_4',
    descFallback: 'Desde el primer día, tu negocio se vuelve imparable. Cada notificación es una oportunidad aprovechada.',
  },
];

const UnifiedHistory: React.FC = () => {
  const { t } = useTranslation();
  const [reducedMotion, setReducedMotion] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [videoEnded, setVideoEnded] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 3;
    }
  }, []);

  return (
    <section className="ruray-history">
      <style>{`
        .ruray-history {
          position: relative;
          background: var(--bg-dark);
          overflow: hidden;
        }

        .rh-intro {
          position: relative;
          min-height: 88vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 6rem 2rem;
        }

        .rh-bg-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
          pointer-events: none;
          transition: opacity 2s ease;
          transform: translateY(80px);
        }

        .rh-bg-fade {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background: linear-gradient(180deg, var(--bg-dark) 0%, transparent 35%, transparent 68%, var(--bg-dark) 92%);
          transform: translateY(80px);
        }

        .rh-blob {
          position: absolute;
          top: 5%;
          left: 50%;
          transform: translateX(-50%);
          width: 700px;
          height: 700px;
          border-radius: 50%;
          background: radial-gradient(circle, var(--accent-teal-dim) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .rh-title {
          position: relative;
          z-index: 1;
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: clamp(3rem, 9vw, 7.5rem);
          line-height: 0.98;
          letter-spacing: -0.03em;
          color: var(--text-primary);
          margin: 180px 0 0;
        }

        .rh-title-digital {
          --rh-progress: 0%;
          position: relative;
          display: inline-block;
          color: transparent;
          -webkit-text-fill-color: transparent;
          background: linear-gradient(
            90deg,
            var(--accent-teal) 0%,
            var(--accent-purple) var(--rh-progress),
            #fff var(--rh-progress),
            #fff 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          animation: rh-digital-progress 3.3s 0s ease-in-out forwards;
        }

        @property --rh-progress {
          syntax: '<percentage>';
          inherits: true;
          initial-value: 0%;
        }

        @keyframes rh-digital-progress {
          from { --rh-progress: 0%; }
          to { --rh-progress: 100%; }
        }

        @media (prefers-reduced-motion: reduce) {
          .rh-title-digital {
            --rh-progress: 100%;
            animation: none;
          }
        }

        .rh-subtitle {
          position: relative;
          z-index: 1;
          font-family: var(--font-body);
          font-size: clamp(1rem, 1.6vw, 1.35rem);
          color: var(--text-secondary);
          letter-spacing: 0.01em;
          margin-top: 1.25rem;
        }

        /* Antes: 3 columnas de texto plano separadas por una simple línea
           arriba, sin ícono, sin conexión visual entre pasos, y solo el 3°
           con algo distinto (el chip de prueba) — se sentía desbalanceado y
           seco. Ahora cada paso es una tarjeta (mismo criterio "bento" que
           .why-card en AboutUs.tsx: fondo, borde, relieve, ícono con acento
           propio) y hay una flecha conectando cada tarjeta con la
           siguiente, para que se lea como una secuencia, no 3 bloques
           sueltos. */
        .rh-steps {
          position: relative;
          z-index: 1;
          max-width: 1120px;
          margin: 8rem auto 0;
          padding: 0 2rem clamp(5rem, 9vw, 7rem);
          display: grid;
          grid-template-columns: 1fr auto 1fr auto 1fr;
          align-items: start;
          gap: 1.25rem;
        }

        @media (max-width: 860px) {
          .rh-steps { grid-template-columns: 1fr; gap: 1.75rem; }
          .rh-step-arrow { display: none; }
        }

        .rh-step {
          position: relative;
          overflow: hidden;
          background: var(--bg-elevated);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: 1.85rem;
          box-shadow: 0 24px 48px -30px rgba(0, 0, 0, 0.5);
          transition: transform 0.3s ease, border-color 0.3s ease;
        }

        .rh-step:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--rh-step-accent) 40%, var(--border-lighter));
        }

        .rh-step-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          margin-bottom: 1.1rem;
          background: color-mix(in srgb, var(--rh-step-accent) 14%, transparent);
          color: var(--rh-step-accent);
        }

        .rh-step-eyebrow {
          display: block;
          font-family: var(--font-body);
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--rh-step-accent);
          margin-bottom: 0.75rem;
        }

        .rh-step-title {
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: clamp(1.3rem, 2.2vw, 1.6rem);
          letter-spacing: -0.01em;
          color: var(--text-primary);
          margin: 0 0 0.75rem;
        }

        .rh-step-desc {
          font-family: var(--font-body);
          font-size: 0.95rem;
          line-height: 1.6;
          color: var(--text-secondary);
          margin: 0;
        }

        .rh-step-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          align-self: center;
          color: var(--text-muted);
          opacity: 0.6;
        }

        .rh-proof {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1.5rem;
          padding: 0.55rem 0.9rem;
          border: 1px solid rgba(52, 211, 153, 0.3);
          background: rgba(52, 211, 153, 0.08);
          border-radius: 12px;
          font-family: var(--font-body);
          font-size: 0.85rem;
          color: var(--text-primary);
        }
      `}</style>

      <div className="rh-intro">
        {reducedMotion ? (
          <img src={introBg} className="rh-bg-photo" style={{ opacity: 0.24 }} alt="" />
        ) : (
          <>
            <img
              src={introBg}
              className="rh-bg-photo"
              style={{ opacity: videoEnded ? 0.24 : 0 }}
              alt=""
            />
            <video
              ref={videoRef}
              className="rh-bg-photo"
              style={{ opacity: videoEnded ? 0 : 0.24 }}
              src={introBgVideo}
              poster={introBg}
              autoPlay
              muted
              playsInline
              onLoadedMetadata={() => {
                if (videoRef.current) videoRef.current.playbackRate = 3;
              }}
              onEnded={() => setVideoEnded(true)}
            />
          </>
        )}
        <div className="rh-bg-fade" />
        <ParallaxBlob className="rh-blob" />

        <motion.h1
          className="rh-title"
          initial={{ opacity: 0, y: 24, filter: 'blur(14px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        >
          RURAY <span className="rh-title-digital">DIGITAL</span>
        </motion.h1>

        <motion.p
          className="rh-subtitle"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          {t('about_page.history_tagline', 'El arte de hacerlo bien.')}
        </motion.p>
      </div>

      <div className="rh-steps">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <React.Fragment key={step.titleFallback}>
              <Reveal delay={i * 0.1}>
                <div className="rh-step" style={{ '--rh-step-accent': step.accent } as React.CSSProperties}>
                  <span className="rh-step-icon"><Icon size={22} strokeWidth={2} /></span>
                  <span className="rh-step-eyebrow">{t(step.eyebrow, step.eyebrowFallback)}</span>
                  <h3 className="rh-step-title">{t(`about_page.${step.titleKey}`, step.titleFallback)}</h3>
                  <p className="rh-step-desc">{t(step.descKey, step.descFallback)}</p>
                  {i === 2 && (
                    <span className="rh-proof">
                      {t('about_page.history_proof_badge', 'Cita agendada para mañana 10:00 AM')} <CheckCheck size={16} />
                    </span>
                  )}
                </div>
              </Reveal>
              {/* Flecha de conexión entre pasos — refuerza que esto es una
                  secuencia (problema → solución → resultado), no 3 tarjetas
                  sueltas. Solo en desktop, donde el grid es horizontal. */}
              {i < steps.length - 1 && (
                <div aria-hidden className="rh-step-arrow">
                  <ArrowRight size={20} strokeWidth={2} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default UnifiedHistory;
